/* eslint-disable */
import Table from '../table';
import { loadCSV, lightenOrDarken, isNumeric } from '../utils';
import { Logger } from '../logger';
import iframeDetect from '../iframe-detect';
import { translate, formatMoneyInt, formatInt, formatFloat } from '../language';
import { getColors } from '../colors';
import { scaleLinear } from 'd3-scale';
// import Switch from '../switch';

const t = translate;
const log = new Logger('01_kindergarten_table');

const template = `
  <div class="tsg-container-ui"></div>
  <div class="tsg-container-graphic"></div>
`;

export default function chart(container, opts = {}) {
  const colors = getColors();
  if (!container) {
    log.error('container does not exist');
    return Promise.resolve();
  }

  const options = {
    highlight: null,
    ...opts,
  };

  return Promise.all([
    loadCSV(`${PATH}data/01_kindergarten_table/01_kindergarten_table.csv`),
    loadCSV(`${PATH}data/01_kindergarten_table/descriptions.csv`),
    iframeDetect.checkFixedHeight(),
  ]).then((res) => {
    log.debug('fetched all we need');


    const frmtYear = formatFloat({ digits: 1, decimal: true});
    const frmtMonth = formatFloat({ digits: 1, decimal: true });

    const data = res[0].filter(d => d.status === 'complete');

    data.sort((a, b) => {
      if (t(a.city) < t(b.city)) return -1;
      if (t(a.city) > t(b.city)) return 1;
      return 0;
    });

    data.forEach((d) => {
      d.city = t(d.city);
      d.cost_private = isNumeric(d.cost_private) ? d.cost_private : t(d.cost_private);
      d.cost_public = isNumeric(d.cost_public) ? d.cost_public : t(d.cost_public);
    });

    const descriptions = {};
    res[1].forEach((d) => {
      descriptions[d.column] = t(d.description);
    });

    function cColor(v) {
      if (v === 'yes') return colors.primary;
      if (v === 'no') return colors.secondary;
      return 'transparent';
    }

    function cSymbol(v) {
      if (v === 'yes') return 'check';
      if (v === 'introduced*') return 'check';
      if (v === 'removed*') return 'x';
      if (v === 'no') return 'x';
      return null;
    }

    function textColor(v) {
      // if (v === t('free')) return colors.primary;
      if (v === t('free except meals')) return colors.primary;
      if (v === t('income dependent')) return colors.secondary;
      if (!Number.isNaN(Number(v.replace('€', '')))) return colors.secondary;
      return colors.black0;
    }

    const formatCostValue = (v) => {
      if (isNumeric(v)) {
        return formatMoneyInt()(v);
      }
      return v;
    }

    /*
    function cNote(v) {
      if (v === 'yes') return '';
      if (v === 'introduced*') return '&#x2605;';
      // if (v === 'removed*') return '&ast;';
      if (v === 'removed*') return '';
      if (v === 'no') return '';
      return null;
    }
    */

    const graphColumnLabel = `
      <div class="tsg-table__graph-header-cell">
        <div class="tsg-table__graph-header-cell__label">
          ${t('age groups')}
        </div>
        <div class="tsg-table__graph-header-cell__legend">
          <span class="tsg-table__graph-header-cell__legend__item" style="white-space: nowrap;">
            <span class="tsg-table__graph-header-cell__color-swatch" style="background-color:${colors.tertiary}"></span>
            <span>${t('voluntary')}</span>
          </span>
          <span class="tsg-table__graph-header-cell__legend__item" style="white-space: nowrap;">
            <span class="tsg-table__graph-header-cell__color-swatch" style="background-color:${colors.primary}"></span>
            <span>${t('right to a place')}</span>
          </span>
          <span class="tsg-table__graph-header-cell__legend__item" style="white-space: nowrap;">
            <span class="tsg-table__graph-header-cell__color-swatch" style="background-color:${colors.secondary}"></span>
            <span>${t('obligatory')}</span>
          </span>
        </div>
      </div>
    `;

    const valToLabel = (val, showFullText) => {
      if (val < 1) {
        // round to the nearest half month
        const months = `${Math.round(val * 12 * 2)/2}`;
        return months === 1 ? `1 ${t('month')}` : `${frmtMonth(months)} ${t('months')}`
      }
      if (showFullText) {
        return val === 1 ? `1 ${t('year')}` : `${frmtYear(val)} ${t('years')}`
      }
      return frmtYear(val);
    }

    const graphYearsCount = 7;

    const appendGraphBar = (graphContainer, [rangeMin, rangeMax], colorVal) => {
      if (!rangeMin && rangeMin != 0 || !rangeMax && rangeMax != 0) return;

      const graphBar = document.createElement('div');
      const ageSpan = rangeMax - rangeMin;
      graphBar.classList.add('tsg-table-cell__graph__bar');
      graphBar.style.width = `${ageSpan/graphYearsCount * 100}%`;
      graphBar.style.left = `${rangeMin/graphYearsCount * 100}%`;
      graphBar.style.backgroundColor = colorVal;

      graphContainer.append(graphBar);
    }

    const appendGraphValueLabels = (graphContainer, datum, isFirst) => {
      const graphValuesContainer = document.createElement('div');
      graphValuesContainer.classList.add('tsg-table-cell__graph__valuelabels-container');

      let startVal = Math.min(
        ...[+datum.age_group_can_min, +datum.age_group_right_min, +datum.age_group_must_min].filter(val => !!val)
      );
      if (startVal === Infinity) startVal = 0;

      const endVal = Math.max(
        ...[+datum.age_group_can_max, +datum.age_group_right_max, +datum.age_group_must_max].filter(val => !!val)
      );


      if (startVal) {  
        const start = document.createElement('div');
        start.classList.add('tsg-table-cell__graph__valuelabels-item');
        start.style.left = `${startVal/graphYearsCount * 100}%`;
        start.append(valToLabel(startVal, isFirst));
  
        graphValuesContainer.append(start);  
      }

      if (endVal) {
        const end = document.createElement('div');
        end.classList.add('tsg-table-cell__graph__valuelabels-item');
        end.style.right = `${100 - (endVal/graphYearsCount * 100)}%`;
        end.append(valToLabel(endVal, isFirst));

        graphValuesContainer.append(end);
      }

      graphContainer.append(graphValuesContainer);
    }

    const graphElem = (datum, isFirst) => {
      const graph = document.createElement('div');
      graph.classList.add('tsg-table-cell__graph');

      const graphContainer = document.createElement('div');
      graphContainer.classList.add('tsg-table-cell__graph__container');
      graphContainer.style.backgroundColor = colors.table_no_value;

      // data bars
      appendGraphBar(graphContainer, [+datum.age_group_can_min, +datum.age_group_can_max], colors.tertiary);
      appendGraphBar(graphContainer, [+datum.age_group_right_min, +datum.age_group_right_max], colors.primary);
      appendGraphBar(graphContainer, [+datum.age_group_must_min, +datum.age_group_must_max], colors.secondary);

      // section markers
      [...Array(graphYearsCount)].forEach((__, i) => {
        const graphSection = document.createElement('div');
        graphSection.classList.add('tsg-table-cell__graph__section');
        graphSection.style.width = `${100 / graphYearsCount}%`;
        graphSection.style.left = `${i * (100 / graphYearsCount)}%`;
        graphContainer.append(graphSection);
      });

      graph.append(graphContainer);

      appendGraphValueLabels(graph, datum, isFirst)

      return graph;
    };

    const formatGraphTooltip = d => {
      let tooltipContent = `<strong>${t(d.city)}</strong>
        <p class="tsg-table-tooltip__spacer"></p>
      `;
      if (d.age_group_can_min && d.age_group_can_max) {
        tooltipContent += `
          <p><span class="tsg-table-tooltip__value" style="background-color:${colors.tertiary}">${valToLabel(d.age_group_can_min)} - ${valToLabel(d.age_group_can_max, true)}</span>${t("kids can voluntarily go to public daycare, kindergarten or preschool")}</p>
        `
      }
      if (d.age_group_right_min && d.age_group_right_max) {
        tooltipContent += `
          <p><span class="tsg-table-tooltip__value" style="background-color:${colors.primary}">${valToLabel(d.age_group_right_min)} - ${valToLabel(d.age_group_right_max, true)}</span>${t("kids have a right to public daycare, kindergarten or preschool")}</p>
        `
      }
      if (d.age_group_must_min && d.age_group_must_max) {
        tooltipContent += `
          <p><span class="tsg-table-tooltip__value" style="background-color:${colors.secondary}">${valToLabel(d.age_group_must_min)} - ${valToLabel(d.age_group_must_max, true)}</span>${t("kids must go to public daycare, kindergarten or preschool")}</p>
        `
      }
      return tooltipContent;
    }

    const defaultColumn = {
      align: 'left',
      noValue: true,
      tooltipTextTranslationFunc: t,
    };

    const circleColumn = {
      ...defaultColumn,
      circle: true,
      circleColor: cColor,
      circleSymbol: cSymbol,
      // circleNote: cNote,
    };

    const textColumn = {
      ...defaultColumn,
      text: true,
      textColor,
    };

    const graphColumn = {
      ...defaultColumn,
      graphElem,
    };

    container.style.color = colors.table_text;
    container.innerHTML = template;
    const containerUI = container.querySelector('.tsg-container-ui');
    const containerGraphic = container.querySelector('.tsg-container-graphic');

    containerUI.style.marginBottom = '20px';

    const formatTooltip = (d, valueToolip) => d[valueToolip] ? t(d[valueToolip]) : ""

    return new Table(containerGraphic, {
      data,
      indexSticky: true,
      backgroundColor: colors.background,
      backgroundColorStripes: lightenOrDarken(colors.background, 0.025),
      indexTextColor: colors.table_text_dark,
      highlight: options.highlight,
      highlightColor: colors.highlight,
      columns: [
        {
          key: 'city', label: t('city'), index: true, align: 'left',
        },
        {
          key: 'state_provided', tooltipTextFormatFunc: d => formatTooltip(d, 'state_provided_annotation') , headerTooltip: descriptions.D1, label: t('state provided'), ...circleColumn,
        },
        {
          key: 'right_to_attend', tooltipTextFormatFunc: d => formatTooltip(d, 'right_to_attend_annotation') , headerTooltip: descriptions.D1, label: t('right to attend'), ...circleColumn,
        },
        {
          key: 'cost_public', tooltipTextFormatFunc: d => formatTooltip(d, 'cost_public_annotation') , headerTooltip: descriptions.D1, label: t('cost public'), formatFunction: formatCostValue, bold: true, ...textColumn,
        },
        {
          key: 'cost_private', tooltipTextFormatFunc: d => formatTooltip(d, 'cost_private_annotation') , headerTooltip: descriptions.D1, label: t('cost private'), formatFunction: formatCostValue, bold: true, ...textColumn, textColor: () => colors.secondary,
        },
        {
          key: 'pickup_times', tooltipTextFormatFunc: d => formatTooltip(d, 'pickup_times_annotation') , headerTooltip: descriptions.D1, label: t('maximum times'), ...textColumn,
        },
        {
          headerTooltip: descriptions.D1,
          label: graphColumnLabel,
          tooltipTextFormatFunc: formatGraphTooltip,
          ...graphColumn,
        },
      ],
    });
  });
}
